// src/pages/Welcome.js
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import LoadingScreen from "../components/LoadingScreen";

// ✅ Promo Banners
import ride1 from "../assets/rideshare/ride1.png";
import ride2 from "../assets/rideshare/ride2.png";
import ride3 from "../assets/rideshare/ride3.png";
import home1 from "../assets/homeservices/home1.png";
import home2 from "../assets/homeservices/home2.png";
import beauty1 from "../assets/beauty/beauty1.png";
import beauty2 from "../assets/beauty/beauty2.png";
import beauty3 from "../assets/beauty/beauty3.png";

// ✅ Service Images (existing)
import plumber from "../assets/services/plumber.png";
import electrician from "../assets/services/electrician.png";
import cleaning from "../assets/services/cleaning.png";
import furniture from "../assets/services/furniture-assembly.png";
import handyman from "../assets/services/handyman.png";
import lawn from "../assets/services/lawn-cleaning.png";
import roof from "../assets/services/roof-cleaning.png";
import windows from "../assets/services/window-cleaning.png";
import junk from "../assets/services/junk-removal.png";
import snow from "../assets/services/snow-removal.png";
import barber from "../assets/services/haircut.png";
import manicure from "../assets/services/pedicure-manicure.png";
import nails from "../assets/services/threading-waxing.png";
import makeup from "../assets/services/makeup.png";
import facial from "../assets/services/facial-skincare.png";
import moving from "../assets/services/moving.png";
import detailing from "../assets/services/detailing.png";
import mechanic from "../assets/services/mechanic.png";
import daycare from "../assets/services/child-care.png";


import painter from "../assets/services/painter.png";
import photographer from "../assets/services/event-photographer.png";
import chef from "../assets/services/personal-chef.png";
import carpet from "../assets/services/carpet-cleaning.png";
import pressure from "../assets/services/pressure-washing.png";
import tvMount from "../assets/services/tv-mounting.png";
import appliance from "../assets/services/appliance-repair.png";
import flooring from "../assets/services/flooring-repair.png";
import pool from "../assets/services/pool-cleaning.png";
import bridal from "../assets/services/bridal-makeup.png";
import tutor from "../assets/services/tutor.png";

const API_BASE = process.env.REACT_APP_API_BASE;

// 🔥 ALL job titles — INCLUDING NEW SERVICES
const allJobTitles = [
  "Plumber", "Electrician", "Lawn Cleaning", "Cleaning", "Furniture Assembly",
  "Roof Cleaning", "Barber", "Moving", "Child Care", "Handyman", "Junk Removal",
  "Snow Removal", "Window Cleaning", "Haircut & Styling (Women)",
  "Pedicure & Manicure", "Facial & Skin Care", "Threading / Waxing",
  "Makeup Artist (Event)", "At-Home Spa (Massage)", "Mechanic", "Car Detailing",
  "Painter", "Event Photographer", "Personal Chef", "Carpet Cleaning",
  "Pressure Washing", "TV Mounting", "Appliance Repair", "Flooring Repair",
  "Pool / Hot Tub Cleaning", "Bridal Makeup", "Tutor / Teaching"
];

// 🔥 ALL promo images
const promoImages = [
  ride1, ride2, ride3,
  home1, home2,
  beauty1, beauty2, beauty3
];

// 🔥 Promo quotes
const promoQuotes = [
  "Need a Ride? Request one in seconds.",
  "Connect with locals headed your way.",
  "Don’t wait around. Book a ride that fits your schedule.",
  "Book trusted professionals for your home.",
  "Need a plumber or electrician? We got you.",
  "Beauty services at your doorstep.",
  "Nails, Makeup, Haircuts – done at home.",
  "Glow up without stepping out."
];

// 👉 ALL IMAGES FOR PRELOADING  
const imagesToLoad = [
  ...promoImages,
  plumber, electrician, cleaning, furniture, handyman,
  lawn, roof, windows, junk, snow,
  barber, manicure, nails, makeup, facial,
  moving, detailing, mechanic, daycare,
  painter, photographer, chef, carpet, pressure,
  tvMount, appliance, flooring, pool, bridal, tutor
];

const Welcome = () => {
  const navigate = useNavigate();
  const name = localStorage.getItem("userName") || "there";
  const userEmail = localStorage.getItem("userEmail");

  const [promoIndex, setPromoIndex] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredJobs, setFilteredJobs] = useState([]);
  const [nextJob, setNextJob] = useState(null);
  const [timeLeft, setTimeLeft] = useState(null);
  const [pendingApprovalJob, setPendingApprovalJob] = useState(null);

  // ⭐ LOADING SCREEN  
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState(0);

  // ⭐ PRELOAD IMAGES  
  useEffect(() => {
    let loaded = 0;

    imagesToLoad.forEach((src) => {
      const img = new Image();
      img.src = src;

      img.onload = img.onerror = () => {
        loaded += 1;
        setProgress(Math.round((loaded / imagesToLoad.length) * 100));
        if (loaded === imagesToLoad.length) {
          setTimeout(() => setLoading(false), 300);
        }
      };
    });
  }, []);

  // ⭐ ROTATE PROMOS  
  useEffect(() => {
    const timer = setInterval(() => {
      setPromoIndex((p) => (p + 1) % promoImages.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  // ⭐ FETCH JOBS  
  useEffect(() => {
    if (!userEmail) return;
    const fetchJobs = async () => {
      try {
        const res = await axios.get(`${API_BASE}/api/customers/jobs/user-jobs/${userEmail}`);

        const allJobs = [
          ...(res.data.pending || []),
          ...(res.data.assigned || []),
          ...(res.data.completed || []),
          ...(res.data.cancelled || [])
        ];

        const upcoming = allJobs
          .filter(j =>
            j.scheduledAt &&
            new Date(j.scheduledAt) > new Date() &&
            ["pending", "reopened"].includes(j.status)
          )
          .sort((a, b) => new Date(a.scheduledAt) - new Date(b.scheduledAt))[0];

        setNextJob(upcoming || null);
        const needsApproval = allJobs.find(j => j.status === "worker_completed");
        setPendingApprovalJob(needsApproval || null);

      } catch (err) {
        console.error("❌ Failed to fetch jobs:", err);
      }
    };
    fetchJobs();
  }, [userEmail]);

  // ⭐ COUNTDOWN TIMER  
  useEffect(() => {
    if (!nextJob?.scheduledAt) return;
    
    const interval = setInterval(() => {
      const now = new Date();
      const jobDate = new Date(nextJob.scheduledAt);
      const diff = jobDate - now;

      if (diff <= 0) {
        setTimeLeft("⏰ Job is starting now!");
        clearInterval(interval);
      } else {
        const days = Math.floor(diff / (1000 * 60 * 60 * 24));
        const hrs = Math.floor((diff / (1000 * 60 * 60)) % 24);
        const mins = Math.floor((diff / (1000 * 60)) % 60);
        setTimeLeft(`${days}d ${hrs}h ${mins}m left`);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [nextJob]);

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "Good Morning";
    if (hour < 18) return "Good Afternoon";
    return "Good Evening";
  };

  const handleCardClick = (serviceName) => {
    const lower = serviceName.toLowerCase();
    if (lower.includes("ride")) navigate("/ride-request");
    else if (lower.includes("trip")) navigate("/your-trips");
    else navigate("/customer-dashboard", { state: { selectedService: serviceName } });
  };

   // ✅ ADD THIS BLOCK RIGHT HERE ⬇⬇⬇
  if (loading) {
    return <LoadingScreen progress={progress} />;
  }
  // ✅ ADD THIS BLOCK RIGHT HERE ⬆⬆⬆

  // ⭐ MAIN UI  
  return (
    <div className="min-h-screen bg-white px-4 pb-24">
      
      <div className="text-left pt-6">
        <h1 className="text-2xl font-semibold text-gray-800">
          {getGreeting()}, {name.split(" ")[0]} 👋
        </h1>
      </div>

      {/* ⭐ NEXT JOB */}
      {nextJob && (
        <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-4 mt-6">
          <h2 className="text-base font-semibold text-gray-800">
            📅 Your Next Job: {nextJob.jobTitle}
          </h2>
          <p className="text-sm text-gray-600">
            Scheduled for {new Date(nextJob.scheduledAt).toLocaleString()}
          </p>
          <p className="mt-2 text-green-600 font-semibold text-sm animate-pulse">
            {timeLeft}
          </p>
        </div>
      )}

      {/* ⭐ Pending Approval */}
      {pendingApprovalJob && (
        <div
          onClick={() => navigate("/your-jobs")}
          className="bg-yellow-50 border border-yellow-400 text-yellow-800 rounded-2xl p-4 mt-6 text-center cursor-pointer hover:bg-yellow-100 transition"
        >
          <h2 className="font-semibold">⚠️ Action Required</h2>
          <p className="text-sm">
            Worker marked <strong>{pendingApprovalJob.jobTitle}</strong> complete.
            Please confirm or dispute.
          </p>
        </div>
      )}

      {/* ⭐ Search */}
      <div className="mt-6 relative">
        <input
          type="text"
          placeholder='Search "Plumber" or "Cleaning"...'
          value={searchQuery}
          onChange={(e) => {
            const val = e.target.value;
            setSearchQuery(val);
            const matches = allJobTitles.filter((j) =>
              j.toLowerCase().includes(val.toLowerCase())
            );
            setFilteredJobs(val ? matches : []);
          }}
          className="w-full p-3 rounded-full border border-gray-200 bg-gray-50 text-sm focus:ring-1 focus:ring-green-500 outline-none"
        />

        {filteredJobs.length > 0 && (
          <ul className="absolute left-0 right-0 z-10 bg-white mt-2 rounded-xl shadow-md max-h-60 overflow-y-auto">
            {filteredJobs.map((job, idx) => (
              <li
                key={idx}
                onClick={() => {
                  setSearchQuery("");
                  setFilteredJobs([]);
                  navigate("/customer-dashboard", { state: { selectedService: job } });
                }}
                className="px-4 py-2 hover:bg-green-100 cursor-pointer"
              >
                {job}
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* ⭐ Promo Banner */}
      <div className="mt-8 relative rounded-2xl overflow-hidden">
        <img
          src={promoImages[promoIndex]}
          alt="Promo"
          className="w-full h-56 object-cover"
        />
        <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center px-6 text-center">
          <p className="text-white font-medium text-base">
            {promoQuotes[promoIndex]}
          </p>
        </div>
      </div>

      {/* ⭐ Sections */}
      <Section title="Home Services">
        <ServiceCard title="Plumber" image={plumber} onClick={handleCardClick} />
        <ServiceCard title="Electrician" image={electrician} onClick={handleCardClick} />
        <ServiceCard title="Cleaning" image={cleaning} onClick={handleCardClick} />
        <ServiceCard title="Furniture Assembly" image={furniture} onClick={handleCardClick} />
        <ServiceCard title="Handyman" image={handyman} onClick={handleCardClick} />
        <ServiceCard title="Painter" image={painter} onClick={handleCardClick} />
        <ServiceCard title="Carpet Cleaning" image={carpet} onClick={handleCardClick} />
        <ServiceCard title="Pressure Washing" image={pressure} onClick={handleCardClick} />
      </Section>

      <Section title="Outdoor Work">
        <ServiceCard title="Lawn Cleaning" image={lawn} onClick={handleCardClick} />
        <ServiceCard title="Roof Cleaning" image={roof} onClick={handleCardClick} />
        <ServiceCard title="Window Cleaning" image={windows} onClick={handleCardClick} />
        <ServiceCard title="Junk Removal" image={junk} onClick={handleCardClick} />
        <ServiceCard title="Snow Removal" image={snow} onClick={handleCardClick} />
        <ServiceCard title="Pool / Hot Tub Cleaning" image={pool} onClick={handleCardClick} />
      </Section>

      <Section title="Beauty & Wellness">
        <ServiceCard title="Barber" image={barber} onClick={handleCardClick} />
        <ServiceCard title="Pedicure & Manicure" image={manicure} onClick={handleCardClick} />
        <ServiceCard title="Threading / Waxing" image={nails} onClick={handleCardClick} />
        <ServiceCard title="Makeup Artist" image={makeup} onClick={handleCardClick} />
        <ServiceCard title="Facial & Skin Care" image={facial} onClick={handleCardClick} />
        <ServiceCard title="Bridal Makeup" image={bridal} onClick={handleCardClick} />
      </Section>

      <Section title="Car Services">
        <ServiceCard title="Car Detailing" image={detailing} onClick={handleCardClick} />
        <ServiceCard title="Mechanic" image={mechanic} onClick={handleCardClick} />
      </Section>

      <Section title="Photography & Events">
        <ServiceCard title="Event Photographer" image={photographer} onClick={handleCardClick} />
        <ServiceCard title="Personal Chef" image={chef} onClick={handleCardClick} />
        <ServiceCard title="TV Mounting" image={tvMount} onClick={handleCardClick} />
      </Section>

      <Section title="Repairs">
        <ServiceCard title="Appliance Repair" image={appliance} onClick={handleCardClick} />
        <ServiceCard title="Flooring Repair" image={flooring} onClick={handleCardClick} />
      </Section>

      <Section title="Child & Daycare">
        <div
  onClick={() => handleCardClick("Child Care")}
  className="min-w-[120px] bg-white rounded-xl shadow-sm border border-gray-100 text-center p-2 hover:shadow-md transition cursor-pointer"
>
  <img
    src={daycare}
    className="h-20 w-full object-cover object-top rounded-lg mb-2"
  />
  <p className="text-xs font-medium text-gray-700">Child Care</p>
</div>

        <div
  onClick={() => handleCardClick("Tutor / Teaching")}
  className="min-w-[120px] bg-white rounded-xl shadow-sm border border-gray-100 text-center p-2 hover:shadow-md transition cursor-pointer"
>
  <img
    src={tutor}
    className="h-20 w-full object-cover object-top rounded-lg mb-2"
  />
  <p className="text-xs font-medium text-gray-700">Tutor / Teaching</p>
</div>

      </Section>

    </div>
  );
};

/* 🔹 Reusable Components */
const Section = ({ title, children }) => (
  <div className="mt-10">
    <h2 className="text-lg font-semibold text-gray-800 mb-3">{title}</h2>
    <div className="flex overflow-x-auto gap-3 pb-2">{children}</div>
  </div>
);

const ServiceCard = ({ title, image, onClick }) => (
  <div
    onClick={() => onClick(title)}
    className="min-w-[120px] bg-white rounded-xl shadow-sm border border-gray-100 text-center p-2 hover:shadow-md transition cursor-pointer"
  >
    <img
      src={image}
      alt={title}
      className="h-20 w-full object-cover rounded-lg mb-2 bg-gray-100"
    />
    <p className="text-xs font-medium text-gray-700">{title}</p>
  </div>
);

export default Welcome;
